import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isMockEnabled } from '@/mocks';
import { usePassbook } from '@/shared/lib/passbook';
import { channelQuotaKey } from '@/features/channel-quota/api/billing';
import {
  applyScenario,
  moduleGateFor,
  resourceState,
  shouldRetryMarketing,
  toMarketingError,
  useDemoScenario,
  type DemoScenario,
  type ModuleGate,
  type ResourceState,
} from '@/features/marketing-shared';
import {
  approveSmsCampaign,
  deleteSmsCampaign,
  fetchSmsCampaigns,
  fetchSmsNumbers,
  smsCampaignAction,
  smsMarketingKeys,
} from '../api/sms-marketing-api';
import type { SmsAccountNumber, SmsCampaign, SmsDraftInput } from './types';

/**
 * Estado do módulo de SMS marketing da loja: campanhas e remetentes vêm da
 * API do fixe-api via BFF; as ações atualizam o cache e invalidam o saldo.
 */

interface SmsMarketingContextValue {
  idPassbook: string;
  mockMode: boolean;
  scenario: DemoScenario;
  gate: ModuleGate | null;
  campaigns: SmsCampaign[];
  campaignsState: ResourceState;
  numbers: SmsAccountNumber[];
  numbersState: ResourceState;
  duplicateCampaign: (id: string) => Promise<SmsCampaign>;
  removeCampaign: (id: string) => Promise<void>;
  pauseCampaign: (id: string) => Promise<SmsCampaign>;
  resumeCampaign: (id: string) => Promise<SmsCampaign>;
  cancelCampaign: (id: string) => Promise<SmsCampaign>;
  approveCampaign: (
    input: SmsDraftInput,
    options?: { allowPartial?: boolean },
  ) => Promise<{ campaign: SmsCampaign; leftOut: number }>;
}

const SmsMarketingContext = createContext<SmsMarketingContextValue | null>(null);

function upsertFirst<T extends { id: string }>(list: T[] | undefined, item: T): T[] {
  return [item, ...(list ?? []).filter((row) => row.id !== item.id)];
}

function replaceInPlace<T extends { id: string }>(list: T[] | undefined, item: T): T[] {
  if (!list) return [item];
  return list.some((row) => row.id === item.id) ? list.map((row) => (row.id === item.id ? item : row)) : [item, ...list];
}

export function SmsMarketingProvider({ children }: { children: ReactNode }) {
  const { idPassbook } = usePassbook();
  const mockMode = isMockEnabled();
  const scenario = useDemoScenario();
  const queryClient = useQueryClient();

  const campaignsQuery = useQuery({
    queryKey: smsMarketingKeys.campaigns(idPassbook),
    queryFn: () => fetchSmsCampaigns(idPassbook),
    retry: shouldRetryMarketing,
    staleTime: 15_000,
  });
  const numbersQuery = useQuery({
    queryKey: smsMarketingKeys.numbers(idPassbook),
    queryFn: () => fetchSmsNumbers(idPassbook),
    retry: shouldRetryMarketing,
    staleTime: 5 * 60_000,
  });

  const campaigns = useMemo(
    () => applyScenario(campaignsQuery.data ?? [], resourceState(campaignsQuery, toMarketingError), scenario),
    [campaignsQuery, scenario],
  );
  const numbers = useMemo(
    () => applyScenario(numbersQuery.data ?? [], resourceState(numbersQuery, toMarketingError), scenario),
    [numbersQuery, scenario],
  );

  const gate = useMemo<ModuleGate | null>(() => {
    if (scenario === 'sem-permissao') return { kind: 'forbidden', message: 'Cenário de demonstração.' };
    if (scenario === 'nao-contratado') return { kind: 'not_contracted', message: 'Cenário de demonstração.' };
    return moduleGateFor(campaigns.state.error);
  }, [scenario, campaigns.state.error]);

  const putCampaign = useCallback(
    (campaign: SmsCampaign, mode: 'replace' | 'prepend') => {
      queryClient.setQueryData<SmsCampaign[]>(smsMarketingKeys.campaigns(idPassbook), (old) =>
        mode === 'prepend' ? upsertFirst(old, campaign) : replaceInPlace(old, campaign),
      );
      void queryClient.invalidateQueries({ queryKey: smsMarketingKeys.campaignAll(idPassbook, campaign.id) });
    },
    [idPassbook, queryClient],
  );

  const duplicateCampaign = useCallback(
    async (id: string) => {
      const copy = await smsCampaignAction('duplicate', id, idPassbook);
      putCampaign(copy, 'prepend');
      return copy;
    },
    [idPassbook, putCampaign],
  );

  const removeCampaign = useCallback(
    async (id: string) => {
      await deleteSmsCampaign(id, idPassbook);
      queryClient.setQueryData<SmsCampaign[]>(smsMarketingKeys.campaigns(idPassbook), (old) =>
        (old ?? []).filter((row) => row.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: smsMarketingKeys.campaignAll(idPassbook, id) });
    },
    [idPassbook, queryClient],
  );

  const transition = useCallback(
    async (action: 'pause' | 'resume' | 'cancel', id: string) => {
      const updated = await smsCampaignAction(action, id, idPassbook);
      putCampaign(updated, 'replace');
      if (action === 'cancel') void queryClient.invalidateQueries({ queryKey: channelQuotaKey(idPassbook) });
      return updated;
    },
    [idPassbook, putCampaign, queryClient],
  );

  const pauseCampaign = useCallback((id: string) => transition('pause', id), [transition]);
  const resumeCampaign = useCallback((id: string) => transition('resume', id), [transition]);
  const cancelCampaign = useCallback((id: string) => transition('cancel', id), [transition]);

  const approveCampaign = useCallback(
    async (input: SmsDraftInput, options?: { allowPartial?: boolean }) => {
      const result = await approveSmsCampaign(idPassbook, input, options?.allowPartial === true);
      putCampaign(result.campaign, input.id ? 'replace' : 'prepend');
      void queryClient.invalidateQueries({ queryKey: smsMarketingKeys.campaigns(idPassbook) });
      void queryClient.invalidateQueries({ queryKey: channelQuotaKey(idPassbook) });
      return result;
    },
    [idPassbook, putCampaign, queryClient],
  );

  const value = useMemo<SmsMarketingContextValue>(
    () => ({
      idPassbook,
      mockMode,
      scenario,
      gate,
      campaigns: campaigns.items,
      campaignsState: campaigns.state,
      numbers: numbers.items,
      numbersState: numbers.state,
      duplicateCampaign,
      removeCampaign,
      pauseCampaign,
      resumeCampaign,
      cancelCampaign,
      approveCampaign,
    }),
    [idPassbook, mockMode, scenario, gate, campaigns, numbers, duplicateCampaign, removeCampaign, pauseCampaign, resumeCampaign, cancelCampaign, approveCampaign],
  );

  return <SmsMarketingContext.Provider value={value}>{children}</SmsMarketingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSmsMarketing(): SmsMarketingContextValue {
  const ctx = useContext(SmsMarketingContext);
  if (!ctx) throw new Error('useSmsMarketing deve ficar dentro do módulo de SMS marketing.');
  return ctx;
}
